import { Button, Flex } from '@radix-ui/themes'
import { AtSign, Camera, Facebook, Globe, Instagram, Music2, Youtube } from 'lucide-react'
import { EventsShowMedia } from '@/types/events_show'

function stripLeadingAt(value: string): string {
  return value.replace(/^@+/, '')
}

function hostnameFrom(value: string): string {
  try {
    return new URL(value).hostname.replace(/^www\./, '')
  } catch {
    return value
  }
}

/** Every social link a media group gave us, as buttons that open in a new tab. */
export default function MediaSocials({ media }: { media: EventsShowMedia }) {
  const links: {
    key: string
    href: string
    label: string
    display: string
    icon: React.ReactNode
  }[] = []

  const ICON = 16

  if (media.instagram) {
    const handle = stripLeadingAt(media.instagram)
    links.push({
      key: 'instagram',
      href: `https://instagram.com/${handle}`,
      label: 'Instagram',
      display: `@${handle}`,
      icon: <Instagram size={ICON} />,
    })
  }
  if (media.youtube) {
    links.push({ key: 'youtube', href: media.youtube, label: 'YouTube', display: 'YouTube', icon: <Youtube size={ICON} /> })
  }
  if (media.flickr) {
    links.push({ key: 'flickr', href: media.flickr, label: 'Flickr', display: 'Flickr', icon: <Camera size={ICON} /> })
  }
  if (media.facebook) {
    const isUrl = /^https?:\/\//i.test(media.facebook)
    const href = isUrl ? media.facebook : `https://www.facebook.com/${stripLeadingAt(media.facebook)}`
    links.push({
      key: 'facebook',
      href,
      label: 'Facebook',
      display: isUrl ? 'Facebook' : `@${stripLeadingAt(media.facebook)}`,
      icon: <Facebook size={ICON} />,
    })
  }
  if (media.tiktok) {
    const handle = stripLeadingAt(media.tiktok)
    links.push({
      key: 'tiktok',
      href: `https://www.tiktok.com/@${handle}`,
      label: 'TikTok',
      display: `@${handle}`,
      icon: <Music2 size={ICON} />,
    })
  }
  if (media.threads) {
    const handle = stripLeadingAt(media.threads)
    links.push({
      key: 'threads',
      href: `https://www.threads.net/@${handle}`,
      label: 'Threads',
      display: `@${handle}`,
      icon: <AtSign size={ICON} />,
    })
  }
  if (media.website) {
    links.push({
      key: 'website',
      href: media.website,
      label: 'Website',
      display: hostnameFrom(media.website),
      icon: <Globe size={ICON} />,
    })
  }

  if (links.length === 0) return null

  return (
    <Flex gap="2" align="center" wrap="wrap">
      {links.map(({ key, href, label, display, icon }) => (
        <Button key={key} asChild variant="soft" color="gray" size="2">
          <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
            {icon}
            {display}
          </a>
        </Button>
      ))}
    </Flex>
  )
}
